import { useState, useEffect, useContext } from "react";
import { Plus, Trash2, Globe, Loader2, CheckCircle, X } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../shared/services/api";
import { SidebarContext } from "../../shared/context/SidebarContext";


export default function Dominios() {

    const { isOpen } = useContext(SidebarContext);
    const [dominios, setDominios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [newDomain, setNewDomain] = useState("");
    const [submitting, setSubmitting] = useState(false);


    useEffect(() => { fetchDominios(); }, []);


    const fetchDominios = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/dominios');
            setDominios(data);
        } catch (error) {
            console.error(error);
            Swal.fire('Erro', 'Não foi possível carregar os domínios.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDomain = async (e) => {
        e.preventDefault();
        if (!newDomain) return;
        try {
            setSubmitting(true);
            const { data } = await api.post('/dominios', { url: newDomain });
            setDominios([data, ...dominios]);
            setIsFormOpen(false);
            setNewDomain("");
            Swal.fire({
                icon: 'success',
                title: 'Domínio Cadastrado!',
                text: 'O domínio foi adicionado com sucesso. Lembre-se de apontar o DNS tipo A para o nosso IP da Metriqa.',
                confirmButtonColor: '#6366F1'
            });
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao cadastrar domínio.';
            Swal.fire('Atenção', msg, 'warning');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id, url) => {
        const result = await Swal.fire({
            title: `Excluir ${url}?`,
            text: "Campanhas usando este domínio voltarão para o domínio padrão. Tem certeza?",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#3F3F46',
            confirmButtonText: 'Sim, excluir',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/dominios/${id}`);
                setDominios(dominios.filter(d => d.id !== id));
                Swal.fire('Excluído!', 'O domínio foi removido.', 'success');
            } catch (error) {
                console.error(error);
                Swal.fire('Erro', 'Falha ao excluir o domínio.', 'error');
            }
        }
    };


    return (
        <div className="min-h-screen text-ink-1 flex relative z-0">
            <div className={`transition-all duration-300 ease-out-expo ${isOpen ? "lg:ml-64" : "sm:ml-16"} w-full max-w-full mb-16 sm:mb-0`}>

                <div className="px-5 sm:px-8 pt-8 pb-12 max-w-[1400px] mx-auto space-y-6">

                    <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 animate-fade-in">
                        <div className="space-y-1">
                            <span className="label-overline">Configuração</span>
                            <h1 className="display-2 flex items-center gap-3">
                                Domínios
                                <Globe className="h-7 w-7 text-brand-400" strokeWidth={2.2} />
                            </h1>
                            <p className="text-ink-3 text-sm">
                                Gerencie os domínios <span className="text-ink-2 font-medium">white-label</span> da sua empresa para os links de rotação de grupos do WhatsApp.
                            </p>
                        </div>
                        <button onClick={() => setIsFormOpen(!isFormOpen)} className="btn-primary !py-2.5">
                            <Plus className="h-4 w-4" /> Novo Domínio
                        </button>
                    </header>

                    {isFormOpen && (
                        <div className="surface-card p-6 animate-slide-up">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="heading-3 flex items-center gap-2">
                                    <Globe className="h-4 w-4 text-brand-300" />
                                    Cadastrar Novo Domínio
                                </h2>
                                <button onClick={() => setIsFormOpen(false)} className="p-1 text-ink-4 hover:text-ink-2 hover:bg-white/5 rounded-md transition">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <form onSubmit={handleCreateDomain} className="flex flex-col md:flex-row gap-3">
                                <div className="flex-1 space-y-2">
                                    <label className="label-overline block">URL do domínio / subdomínio</label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            placeholder="ex: grupo.suaempresa.com.br"
                                            value={newDomain}
                                            onChange={(e) => setNewDomain(e.target.value)}
                                            disabled={submitting}
                                            required
                                            className="input-field !py-3 font-mono"
                                        />
                                        <Globe className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-4 pointer-events-none" />
                                    </div>
                                    <p className="text-xs text-ink-4">
                                        Sem <code className="text-brand-300 bg-brand-500/10 px-1.5 py-0.5 rounded">https://</code> nem barra final.
                                    </p>
                                </div>
                                <div className="flex items-end gap-2">
                                    <button type="button" onClick={() => setIsFormOpen(false)} className="btn-ghost !py-3">Cancelar</button>
                                    <button type="submit" disabled={submitting || !newDomain} className="btn-primary !py-3">
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                        Salvar
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="surface-card overflow-hidden">
                        {loading ? (
                            <div className="p-12 grid place-items-center text-ink-4">
                                <Loader2 className="h-7 w-7 animate-spin mb-3 text-brand-400" />
                                <p className="text-sm">Carregando domínios...</p>
                            </div>
                        ) : dominios.length === 0 ? (
                            <div className="p-12 grid place-items-center text-center">
                                <Globe className="h-10 w-10 mb-3 text-ink-5" />
                                <h3 className="heading-3 mb-1">Nenhum domínio configurado</h3>
                                <p className="text-sm text-ink-4 max-w-md">
                                    Cadastre seu domínio para remover a marca da plataforma dos links de rotação de grupos.
                                </p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                    <thead className="bg-surface-3/60 backdrop-blur-sm">
                                        <tr>
                                            <th className="px-6 py-3 text-2xs uppercase tracking-wider font-medium text-ink-4 border-b border-white/[0.06]">Domínio</th>
                                            <th className="px-6 py-3 text-2xs uppercase tracking-wider font-medium text-ink-4 border-b border-white/[0.06]">Status</th>
                                            <th className="px-6 py-3 text-2xs uppercase tracking-wider font-medium text-ink-4 border-b border-white/[0.06]">Cadastro</th>
                                            <th className="px-6 py-3 text-2xs uppercase tracking-wider font-medium text-ink-4 border-b border-white/[0.06] w-20">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-white/[0.04]">
                                        {dominios.map((dominio) => (
                                            <tr key={dominio.id} className="hover:bg-white/[0.02] transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <span className="h-9 w-9 rounded-lg bg-brand-500/15 ring-1 ring-brand-500/25 grid place-items-center text-brand-300">
                                                            <Globe className="h-4 w-4" />
                                                        </span>
                                                        <div>
                                                            <div className="font-semibold text-ink-1">{dominio.url}</div>
                                                            <div className="text-xs text-ink-4 font-mono">https://{dominio.url}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="chip-positive">
                                                        <CheckCircle className="h-3 w-3" /> Ativo no sistema
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-ink-3 tabular-nums">
                                                    {new Date(dominio.data_criacao).toLocaleDateString("pt-BR")}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <button
                                                        onClick={() => handleDelete(dominio.id, dominio.url)}
                                                        title="Remover domínio"
                                                        className="p-2 text-ink-4 hover:bg-danger-500/10 hover:text-danger-400 transition rounded-lg ring-1 ring-transparent hover:ring-danger-500/20"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="glow-card p-6 flex items-start flex-col sm:flex-row gap-4">
                        <span className="h-12 w-12 rounded-xl bg-brand-500/15 ring-1 ring-brand-500/25 grid place-items-center text-brand-300 flex-shrink-0">
                            <Globe className="h-5 w-5" />
                        </span>
                        <div>
                            <h4 className="heading-3 text-brand-200 mb-1">Configuração DNS Type A</h4>
                            <p className="text-ink-3 text-sm leading-relaxed mb-4">
                                Para que os links sob seu domínio abram a plataforma, crie um apontamento DNS tipo <span className="text-ink-1 font-medium">A</span> no seu provedor (Cloudflare, Registro.br, Hostgator…) direcionando o domínio cadastrado para o IP servidor:
                            </p>
                            <div className="inline-flex items-center gap-2 surface-card !p-3 px-4">
                                <span className="text-ink-5 text-xs uppercase tracking-wider mr-2">IP</span>
                                <span className="font-mono text-brand-200 text-sm select-all">147.93.36.33</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
